import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Close,
  LocationOn,
  Schedule,
  Engineering,
  BugReport,
  Thermostat,
  WaterDrop,
  Air,
  Compress,
  WbSunny,
  Speed,
  Fullscreen,
  FullscreenExit,
  Refresh,
  Download,
  Psychology
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api, { stationService, stationTimeseriesService, measurementsService, anomalyService } from '../services/api';
import { convertToCSV, downloadCSV, generateCSVFilename } from '../utils/csvExport';
import StationIngestionInfo from './StationIngestionInfo';
import UnitPreferencesSection from './UnitPreferencesSection';
import { convertValue, getDisplayUnit, getUnitOptions } from '../utils/unitConversions';
import { formatInStationTimezone, getTimezoneAbbreviation } from '../utils/timezoneDisplay';
import AlertRulesTab from './AlertRulesTab';
import ValidationRulesTab from './ValidationRulesTab';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  id: string;
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  api_key?: string;
  mqtt_username?: string;
  is_owner?: boolean;
  available_metrics?: string[];
  unit_preferences?: Record<string, string>;
}

interface StationDetailsProps {
  open: boolean;
  onClose: () => void;
  stationUuid?: string;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`station-tabpanel-${index}`}
    aria-labelledby={`station-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const getSensorIcon = (sensorType: string) => {
  switch (sensorType.toLowerCase()) {
    case 'temperature': return <Thermostat color="primary" />;
    case 'humidity':
    case 'rain':
    case 'reference_et': return <WaterDrop color="primary" />;
    case 'wind_speed': return <Air color="primary" />;
    case 'atmospheric_pressure': return <Compress color="primary" />;
    case 'solar_radiation': return <WbSunny color="primary" />;
    default: return <Speed color="primary" />;
  }
};

const formatSensorName = (key: string) => {
  const sensorNames: Record<string, string> = {
    temperature: 'Temperature',
    humidity: 'Humidity',
    rain: 'Rainfall',
    wind_speed: 'Wind Speed',
    wind_direction: 'Wind Direction',
    atmospheric_pressure: 'Atmospheric Pressure',
    reference_et: 'Reference ET',
    solar_radiation: 'Solar Radiation'
  };
  return sensorNames[key] || key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ');
};

const StationDetails: React.FC<StationDetailsProps> = ({ open, onClose, stationUuid, initialTab = 0 }) => {
  const [station, setStation] = useState<Station | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(initialTab);
  const [timeRange, setTimeRange] = useState<number>(24);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [liveReadings, setLiveReadings] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anomalyData, setAnomalyData] = useState<any>(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalyError, setAnomalyError] = useState<string | null>(null);

  useEffect(() => {
    if (open && stationUuid) {
      // Clear previous data when station changes
      setLiveReadings([]);
      setLastRefresh(null);
      setIsRefreshing(false);
      setAnomalyData(null);
      setAnomalyError(null);
      setAnomalyLoading(false);
      setTimeRange(24);
      setTabValue(initialTab);
      fetchStationDetails();
      fetchTimeSeriesData();
    }
  }, [open, stationUuid]);

  useEffect(() => {
    if (open && stationUuid && (tabValue === 2 || tabValue === 3)) {
      fetchTimeSeriesData();
    }
  }, [timeRange]);

  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);

  // Initial fetch when Live Readings tab is active
  useEffect(() => {
    if (open && stationUuid && tabValue === 1) {
      fetchLiveReadings();
    }
  }, [open, stationUuid, tabValue]);

  // Auto-refresh live readings every 2 minutes when fullscreen mode is active
  useEffect(() => {
    if (open && stationUuid && tabValue === 1 && isFullscreen) {
      // Set up interval for auto-refresh in fullscreen mode
      const interval = setInterval(() => {
        fetchLiveReadings();
      }, 120000); // 120 seconds (2 minutes)

      // Cleanup interval on unmount or when fullscreen/tab/dialog changes
      return () => clearInterval(interval);
    }
  }, [open, stationUuid, tabValue, isFullscreen]);

  const fetchStationDetails = async () => {
    if (!stationUuid) return;
    
    setLoading(true);
    try {
      const data = await stationService.getById(stationUuid);
      setStation(data);
    } catch (error) {
      console.error('Error fetching station details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSeriesData = async () => {
    if (!stationUuid) return;

    try {
      const data = await stationTimeseriesService.getTimeseries(stationUuid, timeRange);
      // Format data for charts
      const formattedData = data.timeseries.map((point: any) => ({
        ...point,
        timestampMs: new Date(point.timestamp).getTime()
      }));
      setTimeSeriesData(formattedData);
    } catch (error) {
      console.error('Error fetching time series data:', error);
    }
  };

  const fetchLiveReadings = async () => {
    if (!stationUuid) return;

    setIsRefreshing(true);
    try {
      // Use the efficient measurements/latest endpoint which fetches only the most recent record
      const data = await measurementsService.getLatest(stationUuid, 1);

      if (data && data.measurements && data.measurements.length > 0) {
        const latest = data.measurements[0];

        // Convert to array of readings for display
        const readings = Object.keys(latest)
          .filter(key => key !== 'timestamp' && latest[key] !== null && latest[key] !== undefined)
          .map(key => ({
            metric: key,
            value: latest[key],
            timestamp: latest.timestamp,
            name: metricConfig[key]?.name || formatSensorName(key),
            unit: metricConfig[key]?.unit || '',
            color: metricConfig[key]?.color || '#00d4ff'
          }));

        setLiveReadings(readings);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error('Error fetching live readings:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchAnomalyData = async () => {
    if (!stationUuid) return;
    setAnomalyLoading(true);
    setAnomalyError(null);
    try {
      const data = await anomalyService.analyze(stationUuid);
      setAnomalyData(data);
    } catch (error: any) {
      setAnomalyError(error?.response?.data?.error || 'Failed to run AI analysis. Please try again.');
    } finally {
      setAnomalyLoading(false);
    }
  };

  // Get available metrics from the data (excluding timestamp and time)
  const getAvailableMetrics = () => {
    if (timeSeriesData.length === 0) return [];

    // Collect all unique metrics from all data points that have at least one non-null value
    const metricsWithValues = new Set<string>();

    timeSeriesData.forEach(point => {
      Object.keys(point).forEach(key => {
        if (key !== 'timestamp' && key !== 'time' && key !== 'timestampMs' && point[key] !== null && point[key] !== undefined) {
          metricsWithValues.add(key);
        }
      });
    });

    return Array.from(metricsWithValues);
  };

  // Metric display configuration
  // Use centralized metric configuration
  const metricConfig = React.useMemo(() => {
    const config: Record<string, { name: string; color: string; unit: string }> = {};
    SUPPORTED_METRICS.forEach(metric => {
      config[metric.internal] = {
        name: metric.userFriendly.charAt(0).toUpperCase() + metric.userFriendly.slice(1).replace(/_/g, ' '),
        color: metric.color,
        unit: metric.unit
      };
    });
    return config;
  }, []);

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };

  const getTimeRangeLabel = () => {
    if (timeRange === 24) return '24-Hour';
    if (timeRange === 48) return '48-Hour';
    if (timeRange === 120) return '5-Day';
    if (timeRange === 720) return 'Monthly';
    return `${timeRange}-Hour`;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!station) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 10001 }}>
        <DialogContent>
          <Typography>Loading station details...</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ zIndex: 10001 }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" component="div">
              {station.name}
            </Typography>
            {station.uuid && (
              <Typography variant="body2" color="text.secondary">
                UUID: {station.uuid}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {station.debug && (
              <Chip icon={<BugReport />} label="Debug Mode" size="small" color="info" />
            )}
            <Chip
              label={station.status}
              size="small"
              color={station.status === 'Online' ? 'success' : station.status === 'Maintenance' ? 'warning' : 'error'}
            />
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Station Info" />
          <Tab label="Live Readings" />
          <Tab label="Time Series" />
          <Tab label="Table View" />
          {/* Only show API & Integration tab if user owns the station (has uuid/api_key) */}
          {station.uuid && station.api_key && (
            <Tab label="API & Integration" />
          )}
          {/* Only show Rules & Alerts tab if user owns the station */}
          {station.uuid && station.api_key && (
            <Tab label="Rules & Alerts" />
          )}
          {station.uuid && station.api_key && (
            <Tab label="AI Analysis" icon={<Psychology sx={{ fontSize: 18 }} />} iconPosition="start" />
          )}
        </Tabs>

        {/* Tab 0: Station Info */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.name}
              </Typography>
            </Grid>
            {/* Only show UUID if user owns the station */}
            {station.uuid && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  UUID
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {station.uuid}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.description || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.location.latitude.toFixed(6)}, {station.location.longitude.toFixed(6)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Elevation
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.elevation ? `${station.elevation} m` : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Timezone
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.timezone || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Provider
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.owner || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Source
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.source || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Visibility
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.public ? 'Public' : 'Private'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.status}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(station.created_at).toLocaleString('en-GB')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Updated At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.updated_at ? new Date(station.updated_at).toLocaleString('en-GB') : 'N/A'}
              </Typography>
            </Grid>
            {station.notes && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {station.notes}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Unit Preferences — owner only */}
          {station.uuid && station.api_key && (
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <UnitPreferencesSection
                stationUuid={station.uuid}
                currentPreferences={station.unit_preferences || null}
                onSaved={(prefs) => setStation({ ...station, unit_preferences: prefs })}
              />
            </Box>
          )}
        </TabPanel>

        {/* Tab 1: Live Readings */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={isFullscreen ? {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.default',
            zIndex: 9999,
            p: 4,
            overflow: 'auto'
          } : {}}>
            {/* Header with Fullscreen and Refresh buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant={isFullscreen ? "h3" : "h6"} gutterBottom>
                  {station.name} - Live Readings
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isRefreshing && <CircularProgress size={16} />}
                  {lastRefresh && (
                    <Typography variant="caption" color="text.secondary">
                      Last updated: {lastRefresh.toLocaleString('en-GB')}
                    </Typography>
                  )}
                </Box>
                {isFullscreen && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Auto-refresh every 2 minutes
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isRefreshing ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={fetchLiveReadings}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
              </Box>
            </Box>

            {/* Live Readings Grid */}
            {liveReadings.length > 0 ? (
              <Grid container spacing={isFullscreen ? 4 : 3}>
                {liveReadings.map((reading) => (
                  <Grid item xs={12} sm={isFullscreen ? 6 : 12} md={isFullscreen ? 4 : 6} lg={isFullscreen ? 3 : 4} key={reading.metric}>
                    <Card sx={{
                      height: '100%',
                      background: isFullscreen
                        ? `linear-gradient(135deg, ${reading.color}20 0%, ${reading.color}10 100%)`
                        : 'background.paper',
                      border: isFullscreen ? `2px solid ${reading.color}40` : 'none'
                    }}>
                      <CardContent sx={{
                        textAlign: 'center',
                        p: isFullscreen ? 4 : 2
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: isFullscreen ? 3 : 2
                        }}>
                          {getSensorIcon(reading.metric)}
                          <Typography
                            variant={isFullscreen ? "h4" : "h6"}
                            sx={{ ml: 1 }}
                          >
                            {reading.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant={isFullscreen ? "h1" : "h3"}
                          sx={{
                            color: reading.color,
                            fontWeight: 700,
                            mb: 1
                          }}
                        >
                          {(() => {
                            const unitPrefs = station.unit_preferences;
                            if (typeof reading.value === 'number' && unitPrefs && unitPrefs[reading.metric] && getUnitOptions(reading.metric)) {
                              return convertValue(reading.value, reading.metric, unitPrefs[reading.metric]).toFixed(2);
                            }
                            return typeof reading.value === 'number' ? reading.value.toFixed(2) : reading.value;
                          })()}
                        </Typography>
                        <Typography
                          variant={isFullscreen ? "h5" : "body1"}
                          color="text.secondary"
                        >
                          {getUnitOptions(reading.metric) ? getDisplayUnit(reading.metric, station.unit_preferences) : reading.unit}
                        </Typography>
                        {reading.timestamp && (
                          <Typography
                            variant={isFullscreen ? "body1" : "caption"}
                            color="text.secondary"
                            sx={{ mt: isFullscreen ? 2 : 1 }}
                          >
                            {formatInStationTimezone(reading.timestamp, station.timezone)}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">
                  No live readings available yet. Data will refresh every 10 seconds.
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: Time Series Charts */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">
                {getTimeRangeLabel()} Sensor Measurements
              </Typography>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="time-range-label">Time Range</InputLabel>
                <Select
                  labelId="time-range-label"
                  id="time-range-select"
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  label="Time Range"
                  MenuProps={{
                    style: { zIndex: 10002 }
                  }}
                >
                  <MenuItem value={24}>Last 24 Hours</MenuItem>
                  <MenuItem value={48}>Last 48 Hours</MenuItem>
                  <MenuItem value={120}>Last 5 Days</MenuItem>
                  <MenuItem value={720}>Last Month</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {timeSeriesData.length > 0 ? (
              <Box>
                {(() => {
                  const availableMetrics = getAvailableMetrics();

                  // Default colors for metrics without configuration
                  const defaultColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a28bd4', '#58d4ff'];

                  return availableMetrics.map((metric, index) => {
                    const config = metricConfig[metric] || {
                      name: metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                      color: defaultColors[index % defaultColors.length],
                      unit: ''
                    };

                    // Apply unit conversion if station has preferences
                    const unitPrefs = station.unit_preferences;
                    const displayUnit = getDisplayUnit(metric, unitPrefs);
                    const hasConversion = getUnitOptions(metric) !== null;
                    const chartUnit = hasConversion ? displayUnit : config.unit;

                    // Convert data for this metric
                    const chartData = hasConversion && unitPrefs && unitPrefs[metric]
                      ? timeSeriesData.map(point => ({
                          ...point,
                          [metric]: point[metric] != null ? convertValue(point[metric], metric, unitPrefs[metric]) : null,
                        }))
                      : timeSeriesData;

                    return (
                      <Box key={metric} sx={{ mb: index < availableMetrics.length - 1 ? 4 : 0 }}>
                        <Typography variant="h6" gutterBottom>
                          {config.name} {chartUnit && `(${chartUnit})`}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="timestampMs"
                              type="number"
                              scale="time"
                              domain={['dataMin', 'dataMax']}
                              tickFormatter={(ts) => new Date(ts).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                timeZone: station.timezone || 'UTC',
                              })}
                            />
                            <YAxis />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#333333',
                                border: '1px solid #555555',
                                borderRadius: '4px',
                                color: '#ffffff'
                              }}
                              labelStyle={{ color: '#ffffff' }}
                              itemStyle={{ color: '#ffffff' }}
                              labelFormatter={(ts) => new Date(ts).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                timeZone: station.timezone || 'UTC',
                              }) + ` (${getTimezoneAbbreviation(station.timezone)})`}
                            />
                            <Line
                              type="monotone"
                              dataKey={metric}
                              stroke={config.color}
                              strokeWidth={2}
                              name={chartUnit ? `${config.name} (${chartUnit})` : config.name}
                              connectNulls={true}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    );
                  });
                })()}
              </Box>
            ) : (
              <Typography color="text.secondary">
                No time series data available for this station.
              </Typography>
            )}
          </Box>
        </TabPanel>

        {/* Tab 3: Table View with CSV Export */}
        <TabPanel value={tabValue} index={3}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Data Table
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="table-time-range-label">Time Range</InputLabel>
                  <Select
                    labelId="table-time-range-label"
                    id="table-time-range-select"
                    value={timeRange}
                    onChange={handleTimeRangeChange}
                    label="Time Range"
                    MenuProps={{
                      style: { zIndex: 10002 }
                    }}
                  >
                    <MenuItem value={24}>Last 24 Hours</MenuItem>
                    <MenuItem value={48}>Last 48 Hours</MenuItem>
                    <MenuItem value={120}>Last 5 Days</MenuItem>
                    <MenuItem value={720}>Last Month</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Download />}
                  sx={{ color: '#000000' }}
                  onClick={async () => {
                    if (timeSeriesData.length > 0 && station && stationUuid) {
                      // Trigger backend logging by calling the API with export=true parameter
                      try {
                        const exportUrl = `/stations/${stationUuid}/timeseries/?hours=${timeRange}&export=true`;
                        await api.get(exportUrl);
                      } catch (error) {
                        console.error('Failed to log export event:', error);
                        // Continue with export even if logging fails
                      }

                      // Proceed with CSV generation and download using existing data
                      const stationData = {
                        id: station.uuid || 'public',
                        name: station.name,
                        description: station.description,
                        timeseries: timeSeriesData,  // Pass all data dynamically
                        period_hours: timeRange,
                        data_points: timeSeriesData.length
                      };
                      const csv = convertToCSV(stationData, station.unit_preferences, station.timezone);
                      // Use station name only if UUID not available (public station)
                      const filename = generateCSVFilename(station.name);
                      downloadCSV(csv, filename);
                    }
                  }}
                  disabled={timeSeriesData.length === 0}
                >
                  Export to CSV
                </Button>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  max 500 records
                </Typography>
              </Box>
            </Box>

            {timeSeriesData.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      {getAvailableMetrics().map((metric) => {
                        const config = metricConfig[metric] || {
                          name: metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                          unit: ''
                        };
                        const hasConv = getUnitOptions(metric) !== null;
                        const displayUnit = hasConv ? getDisplayUnit(metric, station.unit_preferences) : config.unit;
                        return (
                          <TableCell key={metric} align="right">
                            {config.name} {displayUnit ? `(${displayUnit})` : ''}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeSeriesData.slice().reverse().map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatInStationTimezone(row.timestamp, station.timezone)}</TableCell>
                        {getAvailableMetrics().map((metric) => {
                          const val = row[metric];
                          if (val === null || val === undefined) return <TableCell key={metric} align="right">N/A</TableCell>;
                          const unitPrefs = station.unit_preferences;
                          const hasConv = unitPrefs && unitPrefs[metric] && getUnitOptions(metric);
                          const displayVal = hasConv ? convertValue(val, metric, unitPrefs![metric]) : val;
                          return (
                            <TableCell key={metric} align="right">
                              {displayVal.toFixed(2)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No data available to display in table.
              </Typography>
            )}
          </Box>
        </TabPanel>

        {/* Tab 4: API & Integration - Only render if user owns station */}
        {station.uuid && station.api_key && (
          <TabPanel value={tabValue} index={4}>
            <StationIngestionInfo
              stationUuid={station.uuid}
              apiKey={station.api_key}
            />
          </TabPanel>
        )}

        {/* Tab 5: Rules & Alerts */}
        {station.uuid && station.api_key && (
          <TabPanel value={tabValue} index={5}>
            <ValidationRulesTab
              stationUuid={station.uuid}
              availableMetrics={station.available_metrics || []}
            />
          </TabPanel>
        )}

        {/* Tab 6: AI Analysis */}
        {station.uuid && station.api_key && (
          <TabPanel value={tabValue} index={6}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Psychology /> AI-Powered Anomaly Detection
                </Typography>
                <Button
                  variant="contained"
                  onClick={fetchAnomalyData}
                  disabled={anomalyLoading}
                  startIcon={anomalyLoading ? <CircularProgress size={18} /> : <Psychology />}
                >
                  {anomalyLoading ? 'Analyzing...' : anomalyData ? 'Re-run Analysis' : 'Run Analysis'}
                </Button>
              </Box>


              {anomalyError && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.dark', borderRadius: 1, color: 'white' }}>
                  <Typography variant="body2">{anomalyError}</Typography>
                </Box>
              )}

              {anomalyData && (
                <Box>
                  {/* Summary */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                    <Typography variant="body2">
                      Station: <strong>{anomalyData.station_name}</strong> &bull; Period: last {anomalyData.period_hours}h &bull; Records analyzed: {anomalyData.total_records}
                    </Typography>
                  </Box>

                  {/* AI Interpretation */}
                  {anomalyData.ai_interpretation && (
                    <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Psychology fontSize="small" /> AI Interpretation
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line', '& strong': { fontWeight: 600 } }}
                        dangerouslySetInnerHTML={{
                          __html: anomalyData.ai_interpretation.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                      />
                    </Box>
                  )}

                  {/* Statistical Details */}
                  {anomalyData.statistical_analysis && Object.keys(anomalyData.statistical_analysis).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Statistical Analysis (Z-score)</Typography>
                      {Object.entries(anomalyData.statistical_analysis).map(([metric, analysis]: [string, any]) => (
                        <Box key={metric} sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {metricConfig[metric]?.name || metric}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mean: {analysis.mean} &bull; Std: {analysis.std} &bull; Anomalies: {analysis.anomaly_count}
                          </Typography>
                          {analysis.anomalies && analysis.anomalies.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {analysis.anomalies.slice(0, 5).map((a: any, i: number) => (
                                <Typography key={i} variant="caption" display="block" sx={{ color: 'warning.main' }}>
                                  {new Date(a.timestamp).toLocaleString()} — value: {a.value} (z-score: {a.z_score})
                                </Typography>
                              ))}
                              {analysis.anomalies.length > 5 && (
                                <Typography variant="caption" color="text.secondary">
                                  ...and {analysis.anomalies.length - 5} more
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* No anomalies */}
                  {anomalyData.statistical_analysis && Object.keys(anomalyData.statistical_analysis).length === 0 && (
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No anomalies detected in the last {anomalyData.period_hours} hours. All readings are within normal ranges.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {!anomalyData && !anomalyLoading && !anomalyError && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Psychology sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">
                    Click "Run Analysis" to start AI-powered anomaly detection on this station's data.
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StationDetails;